import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from '../lib/sanitize';
import { Mail, MailOpen, Send, Clock, CheckCircle2, XCircle, Edit, Trash2, Loader2, Paperclip, RefreshCw, AlertCircle, AlertTriangle, FileText, Plus, Users, User, Check, ChevronsUpDown, Search, Inbox, Settings, Image, Eye, Flag, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import HtmlEditor from '../components/HtmlEditor';
import AttachmentPicker from '../components/AttachmentPicker';
import DocumentLightbox from '../components/DocumentLightbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

// Team e-mailadres dat standaard in CC staat wanneer "Team" is aangevinkt
const TEAM_EMAIL = 'info@solar-engineering.be';

const Mails = () => {
  const [activeTab, setActiveTab] = useState('queued');
  const [emails, setEmails] = useState([]);
  const [mailSearch, setMailSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  // Inbox state
  const [inboxEmails, setInboxEmails] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxSearch, setInboxSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [selectedInboxEmail, setSelectedInboxEmail] = useState(null);
  const [inboxDetailOpen, setInboxDetailOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyForm, setReplyForm] = useState({ subject: '', body: '' });
  const [sendingReply, setSendingReply] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyCcIncludeTeam, setReplyCcIncludeTeam] = useState(false);
  const [replyCcTeamEmails, setReplyCcTeamEmails] = useState([]);
  const [replyCcExtraInput, setReplyCcExtraInput] = useState('');
  const [replyCcExtraEmails, setReplyCcExtraEmails] = useState([]);

  const buildReplyCcList = () => {
    const cc = [];
    if (replyCcIncludeTeam) cc.push(TEAM_EMAIL);
    cc.push(...replyCcTeamEmails, ...replyCcExtraEmails);
    return [...new Set(cc.filter(e => e && e.includes('@')))];
  };
  const [composeFiles, setComposeFiles] = useState([]);
  const [sendingCompose, setSendingCompose] = useState(false);

  // Lees files in als base64 bijlagen voor verzending
  const readFilesAsAttachments = async (files) => {
    const totalSize = files.reduce((s, f) => s + f.size, 0);
    if (totalSize > 15 * 1024 * 1024) {
      throw new Error('Bijlagen samen groter dan 15MB');
    }
    return Promise.all(files.map(f => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ name: f.name, base64: reader.result.split(',')[1], content_type: f.type || 'application/octet-stream' });
      reader.onerror = reject;
      reader.readAsDataURL(f);
    })));
  };
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Signature state
  const [signatureUrl, setSignatureUrl] = useState('');
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editForm, setEditForm] = useState({
    to_email: '',
    to_name: '',
    subject: '',
    body_html: '',
    cc_emails: []
  });
  const [editCcList, setEditCcList] = useState([]); // Alle CC adressen (checkbox + extra)
  const [editCcExtraInput, setEditCcExtraInput] = useState('');
  const [editAttachmentDocs, setEditAttachmentDocs] = useState([]); // Beschikbare offerte-documenten
  const [editSelectedDocIds, setEditSelectedDocIds] = useState([]); // Aangevinkte bijlagen
  const [editLightboxDoc, setEditLightboxDoc] = useState(null); // Grote popup viewer
  const [saving, setSaving] = useState(false);

  // Voorbeeldweergave: exact zoals de klant de mail ontvangt (incl. PDF-preview)
  const [previewEmail, setPreviewEmail] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewDocs, setPreviewDocs] = useState([]);
  const [previewLightboxDoc, setPreviewLightboxDoc] = useState(null);

  // Inbox email verwijderen
  const [deleteInboxId, setDeleteInboxId] = useState(null);

  // New email compose state
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  // CC state: Team (info@) + individuele medewerkers + extra adressen + extra klant-emails
  const [ccIncludeTeam, setCcIncludeTeam] = useState(false);
  const [ccTeamEmails, setCcTeamEmails] = useState([]);
  const [ccExtraEmails, setCcExtraEmails] = useState([]);
  const [ccExtraInput, setCcExtraInput] = useState('');
  const [ccCustomerExtra, setCcCustomerExtra] = useState([]);
  const [ccPopoverOpen, setCcPopoverOpen] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [composeForm, setComposeForm] = useState({
    customer_id: '',
    to_email: '',
    to_name: '',
    subject: '',
    body_html: ''
  });
  const [composing, setComposing] = useState(false);
  const [syncErrorAccounts, setSyncErrorAccounts] = useState([]);

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer => {
    if (!customerSearchQuery) return true;
    const searchLower = customerSearchQuery.toLowerCase();
    const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase();
    const email = (customer.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  useEffect(() => {
    if (activeTab === 'inbox') {
      fetchInbox();
    } else if (activeTab !== 'signature') {
      fetchEmails();
    }
  }, [activeTab]);

  useEffect(() => {
    // Fetch customers and team members when compose dialog opens
    if (composeDialogOpen) {
      fetchCustomers();
      fetchTeamUsers();
    }
  }, [composeDialogOpen]);

  useEffect(() => {
    // Re-fetch inbox when flag filter changes
    if (activeTab === 'inbox') {
      fetchInbox();
    }
  }, [flaggedOnly]);

  useEffect(() => {
    // Fetch signature settings on mount
    fetchSignature();
    fetchUnreadCount();
    fetchSyncErrors();
  }, []);

  const fetchSyncErrors = async () => {
    try {
      const res = await api.get('/email-accounts');
      const accounts = res.data || [];
      setSyncErrorAccounts(accounts.filter(a => a.is_active && (a.last_sync_status || '').startsWith('error')));
    } catch (error) {
      // Geen toegang tot accounts - banner niet tonen
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get('/inbox/unread/count');
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchInbox = async () => {
    setInboxLoading(true);
    try {
      const params = new URLSearchParams();
      if (inboxSearch) params.set('search', inboxSearch);
      if (flaggedOnly) params.set('flagged', 'true');
      const qs = params.toString();
      const res = await api.get(`/inbox${qs ? `?${qs}` : ''}`);
      setInboxEmails(res.data.emails || []);
    } catch (error) {
      console.error('Error fetching inbox:', error);
      toast.error('Kon inbox niet laden');
    } finally {
      setInboxLoading(false);
    }
  };

  // Toggle gelezen/ongelezen status van een inbox email
  const toggleInboxRead = async (email, e) => {
    e.stopPropagation();
    try {
      if (email.is_read) {
        await api.put(`/inbox/${email.id}/unread`);
      } else {
        await api.put(`/inbox/${email.id}/read`);
      }
      setInboxEmails(prev => prev.map(m => m.id === email.id ? { ...m, is_read: !email.is_read } : m));
      fetchUnreadCount();
    } catch (error) {
      toast.error('Kon status niet wijzigen');
    }
  };

  // Toggle vlagje (belangrijke mail)
  const toggleInboxFlag = async (email, e) => {
    e.stopPropagation();
    try {
      const res = await api.put(`/inbox/${email.id}/flag`);
      const isFlagged = res.data.is_flagged;
      setInboxEmails(prev => prev.map(m => m.id === email.id ? { ...m, is_flagged: isFlagged } : m));
    } catch (error) {
      toast.error('Kon vlag niet wijzigen');
    }
  };

  const fetchSignature = async () => {
    try {
      const res = await api.get('/email-signature');
      setSignatureUrl(res.data.signature_url || '');
      setSignatureEnabled(res.data.signature_enabled || false);
    } catch (error) {
      console.error('Error fetching signature:', error);
    }
  };

  const saveSignature = async () => {
    setSavingSignature(true);
    try {
      await api.put('/email-signature', {
        signature_url: signatureUrl,
        signature_enabled: signatureEnabled
      });
      toast.success('Handtekening opgeslagen');
    } catch (error) {
      toast.error('Kon handtekening niet opslaan');
    } finally {
      setSavingSignature(false);
    }
  };

  const openInboxEmail = async (email) => {
    try {
      // Fetch full email content
      const res = await api.get(`/inbox/${email.id}`);
      setSelectedInboxEmail(res.data);
      setInboxDetailOpen(true);
      
      // Mark as read
      if (!email.is_read) {
        await api.put(`/inbox/${email.id}/read`);
        fetchUnreadCount();
        // Update local state
        setInboxEmails(prev => prev.map(e => 
          e.id === email.id ? { ...e, is_read: true } : e
        ));
      }
    } catch (error) {
      toast.error('Kon email niet openen');
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data.filter(c => c.email)); // Only customers with email
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchTeamUsers = async () => {
    try {
      const res = await api.get('/users');
      const users = res.data.filter(u => u.email && u.email.includes('@'));
      setTeamUsers(users);
      return users;
    } catch (error) {
      console.error('Error fetching team users:', error);
      return [];
    }
  };

  // Standaard aangevinkte medewerkers in CC: Charli en Jorik
  const DEFAULT_CC_NAMES = ['charli', 'jorik'];
  const defaultCcUserEmails = (users) =>
    users
      .filter(u =>
        DEFAULT_CC_NAMES.includes((u.first_name || '').toLowerCase().trim()) ||
        DEFAULT_CC_NAMES.some(n => (u.email || '').toLowerCase().startsWith(n))
      )
      .map(u => u.email);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'queued' ? 'queued' : activeTab === 'sent' ? 'sent' : null;
      const params = status ? `?status=${status}` : '';
      const res = await api.get(`/email-queue${params}`);
      setEmails(res.data);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Kon emails niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (emailId) => {
    setSendingId(emailId);
    try {
      await api.post(`/email-queue/${emailId}/send`);
      toast.success('Email succesvol verzonden!');
      fetchEmails();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Kon email niet verzenden');
    } finally {
      setSendingId(null);
    }
  };

  const handleDeleteEmail = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/email-queue/${deleteId}`);
      toast.success('Email verwijderd');
      setDeleteId(null);
      fetchEmails();
    } catch (error) {
      toast.error('Kon email niet verwijderen');
    }
  };

  // Inbox email verwijderen (soft delete in CRM)
  const handleDeleteInboxEmail = async () => {
    if (!deleteInboxId) return;
    
    try {
      await api.delete(`/inbox/${deleteInboxId}`);
      toast.success('Email verwijderd uit inbox');
      setDeleteInboxId(null);
      setInboxDetailOpen(false);
      fetchInbox();
      fetchUnreadCount();
    } catch (error) {
      toast.error('Kon email niet verwijderen');
    }
  };

  // Open voorbeeldweergave: toont de mail exact zoals de klant hem ontvangt
  const openPreviewDialog = async (email) => {
    setPreviewEmail(email);
    setPreviewDialogOpen(true);
    setPreviewPdfUrl(null);
    setPreviewDocs([]);
    
    // PDF-bijlage laden voor inline preview
    if (email.has_attachment) {
      try {
        const res = await api.get(`/email-queue/${email.id}/attachment`, { responseType: 'blob' });
        setPreviewPdfUrl(URL.createObjectURL(res.data));
      } catch (e) {
        console.error('Kon PDF bijlage niet laden', e);
      }
    }
    
    // Extra document-bijlagen (foto's, legplan) laden
    if (email.attachment_document_ids?.length > 0 && ['offerte', 'installation_offerte'].includes(email.reference_type) && email.reference_id) {
      try {
        const res = await api.get(`/documents?entity_type=${email.reference_type}&entity_id=${email.reference_id}`);
        setPreviewDocs((res.data || []).filter(d => email.attachment_document_ids.includes(d.id)));
      } catch (e) { /* geen extra bijlagen */ }
    }
  };

  const closePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setPreviewEmail(null);
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    setPreviewDocs([]);
  };

  // Zelfde styling als de e-mail bij de klant (links, paragrafen, kleuren)
  const processPreviewHtml = (html) => {
    if (!html) return '';
    let processed = html.replace(
      /<a\s+href="([^"]+)"([^>]*)>/gi,
      '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #ea580c; text-decoration: underline;"$2>'
    );
    processed = processed.replace(/<p>/gi, '<p style="margin-bottom: 1em; line-height: 1.6; color: #1f2937; text-align: left;">');
    return processed;
  };

  const handleEditClick = (email) => {
    setEditingEmail(email);
    setEditForm({
      to_email: email.to_email,
      to_name: email.to_name || '',
      subject: email.subject,
      body_html: email.body_html,
      cc_emails: email.cc_emails || []
    });
    setEditCcExtraInput('');
    // CC: bestaande CC's tonen; indien leeg -> standaard Team + Charli + Jorik aanvinken
    const existingCc = email.cc_emails || [];
    setEditCcList(existingCc);
    fetchTeamUsers().then(users => {
      if (existingCc.length === 0) {
        setEditCcList([TEAM_EMAIL, ...defaultCcUserEmails(users)]);
      }
    });
    // Bijlagen: aangevinkte documenten + beschikbare offerte-documenten ophalen
    setEditSelectedDocIds(email.attachment_document_ids || []);
    setEditAttachmentDocs([]);
    if (['offerte', 'installation_offerte'].includes(email.reference_type) && email.reference_id) {
      api.get(`/documents?entity_type=${email.reference_type}&entity_id=${email.reference_id}`)
        .then((res) => setEditAttachmentDocs(res.data || []))
        .catch(() => {});
    }
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEmail) return;
    
    setSaving(true);
    try {
      const ccList = [...new Set(editCcList.filter(e => e && e.includes('@')))];
      await api.put(`/email-queue/${editingEmail.id}`, {
        ...editForm,
        cc_emails: ccList,
        attachment_document_ids: editSelectedDocIds
      });
      toast.success('Email bijgewerkt');
      setEditDialogOpen(false);
      setEditingEmail(null);
      fetchEmails();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      let msg = 'Kon email niet bijwerken';
      if (typeof detail === 'string') msg = `Kon email niet bijwerken: ${detail}`;
      else if (Array.isArray(detail) && detail.length) {
        const d = detail[0];
        msg = `Kon email niet bijwerken: ${(d.loc || []).slice(-1)[0] || ''} ${d.msg || ''}`.trim();
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Open compose dialog
  const openComposeDialog = () => {
    setComposeForm({
      customer_id: '',
      to_email: '',
      to_name: '',
      subject: '',
      body_html: ''
    });
    setCcIncludeTeam(true); // Team (info@) standaard aangevinkt
    setCcTeamEmails([]);
    fetchTeamUsers().then(users => setCcTeamEmails(defaultCcUserEmails(users)));
    setCcExtraEmails([]);
    setCcExtraInput('');
    setCcCustomerExtra([]);
    setComposeFiles([]);
    setComposeDialogOpen(true);
  };

  // Direct versturen vanuit compose dialog (met bijlagen)
  const handleSendComposedEmail = async () => {
    if (!composeForm.to_email || composeForm.to_email.trim() === '') {
      toast.error('Vul een ontvanger (Aan) in');
      return;
    }
    if (!composeForm.subject) {
      toast.error('Voer een onderwerp in');
      return;
    }
    if (!composeForm.body_html) {
      toast.error('Voer een bericht in');
      return;
    }
    setSendingCompose(true);
    try {
      const attachments = await readFilesAsAttachments(composeFiles);
      await api.post('/mails/compose', {
        to_email: composeForm.to_email,
        to_name: composeForm.to_name || null,
        cc_emails: buildCcList(),
        subject: composeForm.subject,
        body_html: composeForm.body_html,
        customer_id: composeForm.customer_id || null,
        extra_attachments: attachments
      });
      toast.success('Email verzonden!');
      setComposeDialogOpen(false);
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Kon email niet verzenden');
    } finally {
      setSendingCompose(false);
    }
  };

  // Handle customer selection in compose
  const handleCustomerSelect = (customerId) => {
    setCcCustomerExtra([]);
    if (customerId === 'none') {
      setComposeForm(prev => ({
        ...prev,
        customer_id: '',
        to_email: '',
        to_name: ''
      }));
      return;
    }
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setComposeForm(prev => ({
        ...prev,
        customer_id: customerId,
        to_email: customer.email,
        to_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
      }));
      // Extra e-mailadressen van de klant standaard mee aanvinken
      if (customer.additional_emails?.length > 0) {
        setCcCustomerExtra(customer.additional_emails.filter(e => e && e.includes('@')));
      }
    }
  };

  // Toggle team member email in CC
  const toggleCcTeamEmail = (email) => {
    setCcTeamEmails(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  // Add a free-form extra CC address
  const addCcExtraEmail = () => {
    const email = ccExtraInput.trim();
    if (!email || !email.includes('@')) {
      toast.error('Voer een geldig e-mailadres in');
      return;
    }
    if (!ccExtraEmails.includes(email)) {
      setCcExtraEmails(prev => [...prev, email]);
    }
    setCcExtraInput('');
  };

  // Build the final CC list (dedupe, exclude To)
  const buildCcList = () => {
    const cc = [];
    if (ccIncludeTeam) cc.push(TEAM_EMAIL);
    cc.push(...ccTeamEmails, ...ccCustomerExtra, ...ccExtraEmails);
    const toLower = (composeForm.to_email || '').toLowerCase();
    return [...new Set(cc.filter(e => e && e.includes('@')))].filter(e => e.toLowerCase() !== toLower);
  };

  // Queue new email from compose dialog
  const handleQueueComposedEmail = async () => {
    if (!composeForm.to_email || composeForm.to_email.trim() === '') {
      toast.error('Vul een ontvanger (Aan) in');
      return;
    }
    if (!composeForm.subject) {
      toast.error('Voer een onderwerp in');
      return;
    }
    if (!composeForm.body_html) {
      toast.error('Voer een bericht in');
      return;
    }

    setComposing(true);
    try {
      const ccList = buildCcList();
      await api.post('/email-queue', {
        to_email: composeForm.to_email,
        to_name: composeForm.to_name,
        cc_emails: ccList,
        subject: composeForm.subject,
        body_html: composeForm.body_html,
        customer_id: composeForm.customer_id || null,
        reference_type: 'manual'
      });
      
      toast.success(ccList.length > 0 ? `Email klaargezet (${ccList.length} in CC)` : 'Email klaargezet!');
      setComposeDialogOpen(false);
      fetchEmails();
    } catch (error) {
      console.error('Error queuing email:', error);
      toast.error('Kon email niet klaarzetten');
    } finally {
      setComposing(false);
    }
  };

  // Convert HTML to plain text for editing
  const htmlToPlainText = (html) => {
    if (!html) return '';
    // Convert HTML to text while preserving line breaks
    let text = html
      .replace(/<\/p>/gi, '\n\n')  // Paragraph ends become double newlines
      .replace(/<br\s*\/?>/gi, '\n')  // BR tags become single newlines
      .replace(/<\/div>/gi, '\n')  // Div ends become newlines
      .replace(/<[^>]*>/g, '')  // Remove all other HTML tags
      .replace(/&nbsp;/g, ' ')  // Replace nbsp with spaces
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/\n{3,}/g, '\n\n')  // Collapse multiple newlines
      .trim();
    return text;
  };

  // Simple text to HTML converter - preserves newlines between <p> tags
  const plainTextToHtml = (text) => {
    if (!text) return '';
    return text
      .split('\n\n')
      .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
      .join('\n\n');  // Use double newline to match source formatting
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'queued':
        return <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="w-3 h-3 mr-1" />Klaar voor verzending</Badge>;
      case 'sent':
        return <Badge variant="outline" className="text-green-500 border-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Verzonden</Badge>;
      case 'failed':
        return <Badge variant="outline" className="text-red-500 border-red-500"><XCircle className="w-3 h-3 mr-1" />Mislukt</Badge>;
      default:
        return null;
    }
  };

  const getReferenceLabel = (email) => {
    if (!email.reference_type) return null;
    const typeLabels = {
      'offerte': 'Offerte',
      'factuur': 'Factuur',
      'reiniging_offerte': 'Reiniging Offerte',
      'installatie_offerte': 'Installatie Offerte',
      'internal': 'Intern',
      'manual': 'Handmatig'
    };
    return typeLabels[email.reference_type] || email.reference_type;
  };

  const queuedCount = emails.filter(e => e.status === 'queued').length;
  const normalizedQuery = mailSearch.trim().toLowerCase();
  const visibleEmails = normalizedQuery
    ? emails.filter(e => [e.to_name, e.to_email, e.subject, e.body_html, ...(e.cc_emails || [])]
        .filter(Boolean).join(' ').toLowerCase().includes(normalizedQuery))
    : emails;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col overflow-hidden" data-testid="mails-page">
      {/* Header - Compact and Professional */}
      <div className="flex-shrink-0 bg-background border-b px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-lg font-semibold">Mails</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">Beheer en verstuur emails</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => openComposeDialog('customer')} data-testid="new-email-btn" size="sm">
              <Plus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Nieuwe</span> Email
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchEmails} disabled={loading} title="Vernieuwen">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Alert for queued emails - Slim banner */}
      {activeTab !== 'queued' && queuedCount > 0 && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20">
          <div className="flex items-center gap-2 text-amber-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span className="font-medium">{queuedCount} mail(s) klaar voor verzending</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setActiveTab('queued')} className="text-amber-600 hover:text-amber-700">
            Bekijken →
          </Button>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          {/* Tabs - Compact sidebar style */}
          <div className="flex-shrink-0 border-b bg-muted/30">
            <TabsList className="w-full justify-start rounded-none bg-transparent h-auto p-0 gap-0">
              <TabsTrigger value="queued" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2.5 gap-2">
                <Clock className="w-4 h-4" />
                <span>Klaargezet</span>
                {queuedCount > 0 && (
                  <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs">
                    {queuedCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="sent" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2.5 gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>Verzonden</span>
              </TabsTrigger>
              <TabsTrigger value="all" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2.5 gap-2">
                <Mail className="w-4 h-4" />
                <span>Alle</span>
              </TabsTrigger>
              <TabsTrigger value="signature" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2.5 gap-2">
                <Image className="w-4 h-4" />
                <span>Handtekening</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Inbox Tab - Webmail Style */}
          <TabsContent value="inbox" className="flex-1 overflow-hidden m-0 data-[state=active]:flex flex-col">
            {/* Search bar + flag filter */}
            <div className="flex-shrink-0 p-3 border-b bg-muted/20 flex items-center gap-2">
              <div className="relative max-w-xl flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Zoeken in inbox..."
                  value={inboxSearch}
                  onChange={(e) => setInboxSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchInbox()}
                  className="pl-10 h-9 bg-background"
                />
              </div>
              <Button
                variant={flaggedOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFlaggedOnly(!flaggedOnly)}
                className={flaggedOnly ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
                data-testid="inbox-flagged-filter"
                title="Alleen gevlagde mails tonen"
              >
                <Flag className={`w-4 h-4 mr-1 ${flaggedOnly ? 'fill-current' : ''}`} />
                Gevlagd
              </Button>
            </div>
            
            {syncErrorAccounts.length > 0 && (
              <div className="flex-shrink-0 px-4 py-3 bg-amber-500/10 border-b border-amber-500/30 flex items-start gap-3" data-testid="inbox-sync-error-banner">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-600 dark:text-amber-400">Email synchronisatie werkt niet</p>
                  {syncErrorAccounts.map(a => (
                    <p key={a.id} className="text-muted-foreground mt-0.5">
                      <span className="font-medium">{a.email_address}</span>: {a.last_sync_status?.replace('error: ', '')} — Ga naar <span className="font-medium">Instellingen → Email</span> en voer het wachtwoord opnieuw in.
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Email List */}
            <div className="flex-1 overflow-y-auto">
              {inboxLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : inboxEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <Inbox className="w-12 h-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground font-medium">Geen emails</p>
                  <p className="text-sm text-muted-foreground/70 mt-1 text-center">
                    Verbind je email account in Instellingen
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {inboxEmails.map((email) => (
                    <div 
                      key={email.id} 
                      onClick={() => openInboxEmail(email)}
                      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${!email.is_read ? 'bg-primary/5' : ''}`}
                      data-testid={`inbox-email-${email.id}`}
                    >
                      {/* Unread indicator */}
                      <div className="flex-shrink-0 w-2 pt-2">
                        {!email.is_read && <div className="w-2 h-2 bg-primary rounded-full" />}
                      </div>
                      
                      {/* Email content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className={`text-sm truncate flex items-center gap-1.5 ${!email.is_read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>
                            {email.is_flagged && <Flag className="w-3.5 h-3.5 text-orange-500 fill-orange-500 flex-shrink-0" data-testid={`flag-indicator-${email.id}`} />}
                            {email.from_name || email.from_email?.split('@')[0] || 'Onbekend'}
                          </span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {email.email_date ? formatDate(email.email_date) : ''}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${!email.is_read ? 'font-medium' : 'text-muted-foreground'}`}>
                          {email.subject || '(Geen onderwerp)'}
                        </p>
                        {email.customer_name && (
                          <div className="flex items-center gap-1 mt-1">
                            <User className="w-3 h-3 text-primary" />
                            <span className="text-xs text-primary">{email.customer_name}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Row actions: vlag + gelezen/ongelezen */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => toggleInboxFlag(email, e)}
                          title={email.is_flagged ? 'Vlag verwijderen' : 'Markeer als belangrijk'}
                          data-testid={`flag-toggle-${email.id}`}
                        >
                          <Flag className={`w-4 h-4 ${email.is_flagged ? 'text-orange-500 fill-orange-500' : 'text-muted-foreground/50'}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => toggleInboxRead(email, e)}
                          title={email.is_read ? 'Markeer als ongelezen' : 'Markeer als gelezen'}
                          data-testid={`read-toggle-${email.id}`}
                        >
                          {email.is_read ? (
                            <Mail className="w-4 h-4 text-muted-foreground/50" />
                          ) : (
                            <MailOpen className="w-4 h-4 text-primary" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); setDeleteInboxId(email.id); }}
                          title="Verwijderen uit inbox"
                          data-testid={`delete-inbox-${email.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground/50 hover:text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

        {/* Signature Tab */}
        <TabsContent value="signature" className="flex-1 overflow-y-auto m-0 p-4">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Email Handtekening
                </CardTitle>
                <CardDescription>
                  Voeg een handtekening afbeelding toe die onderaan elke uitgaande email wordt geplaatst
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="signature-enabled">Handtekening inschakelen</Label>
                    <p className="text-sm text-muted-foreground">
                      De handtekening wordt automatisch toegevoegd aan uitgaande emails
                    </p>
                  </div>
                  <Switch
                    id="signature-enabled"
                    checked={signatureEnabled}
                    onCheckedChange={setSignatureEnabled}
                  />
                </div>
                
                <div>
                  <Label htmlFor="signature-file">Handtekening afbeelding (JPG/PNG)</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <Input
                      id="signature-file"
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      disabled={uploadingSignature}
                      data-testid="signature-file-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('Afbeelding is groter dan 5MB');
                          return;
                        }
                        setUploadingSignature(true);
                        try {
                          const fd = new FormData();
                          fd.append('file', file);
                          const res = await api.post('/email-signature/upload', fd, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setSignatureUrl(res.data.signature_url);
                          toast.success('Handtekening geüpload');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Upload mislukt');
                        } finally {
                          setUploadingSignature(false);
                          e.target.value = '';
                        }
                      }}
                    />
                    {uploadingSignature && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload je handtekening rechtstreeks — die wordt veilig opgeslagen en onderaan elke uitgaande mail geplaatst
                  </p>
                </div>
                
                {signatureUrl && (
                <div>
                  <Label>Voorbeeld</Label>
                  <div className="mt-2 p-4 border rounded-lg bg-muted/50">
                    <img 
                      src={signatureUrl} 
                      alt="Handtekening voorbeeld" 
                      className="max-w-[400px] h-auto"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        toast.error('Kon afbeelding niet laden - controleer de URL');
                      }}
                    />
                  </div>
                </div>
              )}
              
              <Button onClick={saveSignature} disabled={savingSignature}>
                {savingSignature ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  'Handtekening opslaan'
                )}
              </Button>
            </CardContent>
          </Card>
          </div>
        </TabsContent>

        {/* Queued/Sent/All Tabs - Webmail Style */}
        <TabsContent value={activeTab === 'queued' || activeTab === 'sent' || activeTab === 'all' ? activeTab : 'queued'} className="flex-1 overflow-y-auto m-0">
          {activeTab !== 'inbox' && activeTab !== 'signature' && (
            <>
              <div className="sticky top-0 z-10 bg-background border-b px-4 py-2">
                <div className="relative max-w-md">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={mailSearch}
                    onChange={(e) => setMailSearch(e.target.value)}
                    placeholder="Zoek op klantnaam, offertenummer, adres..."
                    className="pl-8 h-9"
                    data-testid="mail-search-input"
                  />
                </div>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : visibleEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <Mail className="w-12 h-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground font-medium">
                    {normalizedQuery
                      ? 'Geen mails gevonden voor deze zoekopdracht'
                      : activeTab === 'queued' 
                      ? 'Geen emails klaar voor verzending' 
                      : activeTab === 'sent'
                      ? 'Nog geen emails verzonden'
                      : 'Geen emails gevonden'}
                  </p>
                  {!normalizedQuery && (
                  <p className="text-sm text-muted-foreground/70 mt-1 text-center">
                    Emails verschijnen hier wanneer je via Offertes of Facturen een email klaarzet
                  </p>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {visibleEmails.map((email) => (
                    <div key={email.id} className="p-4 hover:bg-muted/30 transition-colors" data-testid={`email-card-${email.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Status & Badges Row */}
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {getStatusBadge(email.status)}
                            {email.has_attachment && (
                              <Badge variant="secondary" className="text-xs">
                                <Paperclip className="w-3 h-3 mr-1" />
                                Bijlage
                              </Badge>
                            )}
                            {email.attachment_document_ids?.length > 0 && (
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700" data-testid={`extra-attachments-badge-${email.id}`}>
                                <Paperclip className="w-3 h-3 mr-1" />
                                +{email.attachment_document_ids.length} document{email.attachment_document_ids.length > 1 ? 'en' : ''}
                              </Badge>
                            )}
                            {getReferenceLabel(email) && (
                              <Badge variant="outline" className="text-xs">
                                <FileText className="w-3 h-3 mr-1" />
                                {getReferenceLabel(email)}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Subject */}
                          <h3 className="font-medium text-sm truncate">{email.subject}</h3>
                          
                          {/* Recipients */}
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span className="truncate">Aan: {email.to_name ? `${email.to_name}` : email.to_email}</span>
                            {email.cc_emails && email.cc_emails.length > 0 && (
                              <span className="text-blue-500">+{email.cc_emails.length} CC</span>
                            )}
                          </div>
                          
                          {/* Preview */}
                          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-1">
                            {htmlToPlainText(email.body_html).substring(0, 100)}...
                          </p>
                          
                          {/* Timestamps */}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground/70">
                            <span>{formatDate(email.created_at)}</span>
                            {email.sent_at && <span>• Verzonden: {formatDate(email.sent_at)}</span>}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openPreviewDialog(email)}
                            title="Voorbeeld (zoals de klant ontvangt)"
                            data-testid={`preview-email-${email.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {email.status === 'queued' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditClick(email)}
                                title="Bewerken"
                                data-testid={`edit-email-${email.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSendEmail(email.id)}
                                disabled={sendingId === email.id}
                                data-testid={`send-email-${email.id}`}
                              >
                                {sendingId === email.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4 mr-1" />
                                )}
                                <span className="hidden sm:inline">Verzenden</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setDeleteId(email.id)}
                                title="Verwijderen"
                                data-testid={`delete-queued-${email.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-muted-foreground/60 hover:text-red-500" />
                              </Button>
                            </>
                          )}
                          {email.status === 'failed' && (
                            <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendEmail(email.id)}
                              disabled={sendingId === email.id}
                            >
                              {sendingId === email.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-1" />
                              )}
                              Opnieuw
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setDeleteId(email.id)}
                              title="Verwijderen"
                              data-testid={`delete-failed-${email.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-muted-foreground/60 hover:text-red-500" />
                            </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
      </div>

      {/* Edit Email Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Email Bewerken
            </DialogTitle>
            <DialogDescription>
              Pas de email aan voordat je deze verstuurt
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Naam ontvanger</Label>
                <Input
                  value={editForm.to_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, to_name: e.target.value }))}
                  placeholder="Jan Jansen"
                />
              </div>
              <div className="space-y-2">
                <Label>Email adres *</Label>
                <Input
                  type="email"
                  value={editForm.to_email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, to_email: e.target.value }))}
                  placeholder="klant@voorbeeld.nl"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>CC</Label>
              <div className="p-3 border rounded-lg space-y-1.5 max-h-48 overflow-y-auto" data-testid="edit-cc-section">
                {/* Team altijd bovenaan */}
                <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted rounded px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={editCcList.includes(TEAM_EMAIL)}
                    onChange={() => setEditCcList(prev =>
                      prev.includes(TEAM_EMAIL) ? prev.filter(e => e !== TEAM_EMAIL) : [...prev, TEAM_EMAIL]
                    )}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="edit-cc-team-checkbox"
                  />
                  <span className="font-medium">Team</span>
                  <span className="text-muted-foreground text-xs">({TEAM_EMAIL})</span>
                </label>
                {teamUsers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted rounded px-1 py-0.5">
                    <input
                      type="checkbox"
                      checked={editCcList.includes(u.email)}
                      onChange={() => setEditCcList(prev =>
                        prev.includes(u.email) ? prev.filter(e => e !== u.email) : [...prev, u.email]
                      )}
                      className="h-4 w-4 rounded border-gray-300"
                      data-testid={`edit-cc-user-${u.id}`}
                    />
                    <span>{u.first_name} {u.last_name}</span>
                    <span className="text-muted-foreground text-xs">({u.email})</span>
                  </label>
                ))}
              </div>
              {/* Extra CC adressen (niet-team) */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Extra e-mailadres in CC..."
                  value={editCcExtraInput}
                  onChange={(e) => setEditCcExtraInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const em = editCcExtraInput.trim();
                      if (em && em.includes('@')) {
                        setEditCcList(prev => prev.includes(em) ? prev : [...prev, em]);
                        setEditCcExtraInput('');
                      }
                    }
                  }}
                  data-testid="edit-cc-extra-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const em = editCcExtraInput.trim();
                    if (em && em.includes('@')) {
                      setEditCcList(prev => prev.includes(em) ? prev : [...prev, em]);
                      setEditCcExtraInput('');
                    } else {
                      toast.error('Voer een geldig e-mailadres in');
                    }
                  }}
                  title="Toevoegen"
                  data-testid="edit-cc-extra-add-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {editCcList.filter(em => em !== TEAM_EMAIL && !teamUsers.some(u => u.email === em)).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {editCcList.filter(em => em !== TEAM_EMAIL && !teamUsers.some(u => u.email === em)).map(em => (
                    <Badge key={em} variant="secondary" className="text-xs gap-1">
                      {em}
                      <button
                        type="button"
                        onClick={() => setEditCcList(prev => prev.filter(x => x !== em))}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Onderwerp *</Label>
              <Input
                value={editForm.subject}
                onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Onderwerp van de email"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Bericht *</Label>
              <HtmlEditor
                value={editForm.body_html}
                onChange={(html) => setEditForm(prev => ({ ...prev, body_html: html }))}
                placeholder="Typ hier uw bericht..."
              />
            </div>
            
            {editingEmail?.has_attachment && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Paperclip className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{editingEmail.attachment_name}</span>
                <Badge variant="secondary">PDF</Badge>
              </div>
            )}
            
            {/* Extra document bijlagen: thumbnails met checkbox */}
            {(editAttachmentDocs.length > 0 || editSelectedDocIds.length > 0) && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-1.5" data-testid="edit-attachment-options">
                <Label className="text-xs font-medium text-blue-800">Extra bijlagen meesturen (klik = groot bekijken):</Label>
                <AttachmentPicker
                  documents={editAttachmentDocs}
                  selectedIds={editSelectedDocIds}
                  onToggle={(docId) => setEditSelectedDocIds(prev =>
                    prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
                  )}
                  onPreview={setEditLightboxDoc}
                  testIdPrefix="edit-attachment"
                />
                {/* Aangevinkte documenten die niet (meer) in de lijst staan */}
                {editSelectedDocIds.filter(id => !editAttachmentDocs.some(d => d.id === id)).map((id) => (
                  <label key={id} className="flex items-center gap-2 text-sm cursor-pointer text-muted-foreground px-1 py-0.5">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => setEditSelectedDocIds(prev => prev.filter(x => x !== id))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Paperclip className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate italic">Bijlage (document niet meer beschikbaar)</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Email Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze email wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEmail} className="bg-red-600 hover:bg-red-500">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Inbox Email Confirmation */}
      <AlertDialog open={!!deleteInboxId} onOpenChange={() => setDeleteInboxId(null)}>
        <AlertDialogContent data-testid="delete-inbox-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Email Verwijderen uit Inbox</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze email uit de inbox wilt verwijderen? De email wordt alleen uit het CRM verwijderd, niet van de mailserver.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInboxEmail} className="bg-red-600 hover:bg-red-500" data-testid="confirm-delete-inbox">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Voorbeeldweergave Dialog: exact zoals de klant de mail ontvangt */}
      <Dialog open={previewDialogOpen} onOpenChange={(o) => { if (!o) closePreviewDialog(); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Voorbeeld — zo ontvangt de klant deze mail
            </DialogTitle>
            <DialogDescription>
              Dit is exact wat verzonden wordt: tekst, opmaak en bijlagen
            </DialogDescription>
          </DialogHeader>
          {previewEmail && (
            <div className="rounded-lg border bg-white text-gray-900 overflow-hidden">
              {/* Mail header */}
              <div className="border-b bg-gray-50 px-5 py-3 space-y-1">
                <p className="text-sm">
                  <span className="text-gray-500">Aan: </span>
                  <span className="font-medium">{previewEmail.to_name ? `${previewEmail.to_name} <${previewEmail.to_email}>` : previewEmail.to_email}</span>
                </p>
                {previewEmail.cc_emails?.length > 0 && (
                  <p className="text-xs text-gray-600"><span className="text-gray-500">CC: </span>{previewEmail.cc_emails.join(', ')}</p>
                )}
                <p className="text-sm"><span className="text-gray-500">Onderwerp: </span><span className="font-semibold">{previewEmail.subject}</span></p>
              </div>
              {/* Body */}
              <div className="px-5 py-4">
                <div
                  className="text-sm text-gray-800"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif', lineHeight: '1.6', textAlign: 'left', wordWrap: 'break-word', overflowWrap: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(processPreviewHtml(previewEmail.body_html)) }}
                  data-testid="preview-email-body"
                />
                {signatureEnabled && signatureUrl && (
                  <div className="mt-5 pt-4 border-t border-gray-200">
                    <img src={signatureUrl} alt="Handtekening" className="max-w-[380px] h-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>
              {/* Bijlagen */}
              {(previewEmail.has_attachment || previewDocs.length > 0) && (
                <div className="border-t bg-gray-50 px-5 py-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Bijlagen</p>
                  {previewEmail.has_attachment && (
                    <div className="space-y-2" data-testid="preview-pdf-attachment">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Paperclip className="w-4 h-4 text-orange-600" />
                        <span className="font-medium">{previewEmail.attachment_name || 'PDF bijlage'}</span>
                        <Badge variant="secondary" className="bg-orange-100 text-orange-700">PDF</Badge>
                      </div>
                      {previewPdfUrl ? (
                        <iframe src={`${previewPdfUrl}#toolbar=0`} title="PDF voorbeeld" className="w-full h-[420px] rounded border bg-white" />
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-gray-500 py-4 justify-center border rounded bg-white">
                          <Loader2 className="w-4 h-4 animate-spin" /> PDF laden...
                        </div>
                      )}
                    </div>
                  )}
                  {previewDocs.length > 0 && (
                    <div className="space-y-1.5" data-testid="preview-extra-attachments">
                      <p className="text-xs text-gray-500">Extra bijlagen ({previewDocs.length}):</p>
                      <div className="grid grid-cols-3 gap-2">
                        {previewDocs.map((doc) => (
                          <button
                            key={doc.id}
                            type="button"
                            onClick={() => setPreviewLightboxDoc({ url: doc.r2_public_url || `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`, filename: doc.filename })}
                            className="text-left"
                            title="Klik om groot te bekijken"
                          >
                            {/\.(jpe?g|png|webp|gif)$/i.test(doc.filename || '') && doc.r2_public_url ? (
                              <img src={doc.r2_public_url} alt={doc.filename} className="w-full h-20 object-cover rounded border" loading="lazy" />
                            ) : (
                              <div className="w-full h-20 rounded border bg-white flex items-center justify-center">
                                <FileText className="w-6 h-6 text-red-500" />
                              </div>
                            )}
                            <p className="text-[11px] truncate mt-0.5 text-gray-600">{doc.filename}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePreviewDialog} data-testid="preview-close-btn">Sluiten</Button>
            {previewEmail?.status === 'queued' && (
              <Button
                onClick={async () => { const id = previewEmail.id; closePreviewDialog(); await handleSendEmail(id); }}
                disabled={sendingId === previewEmail?.id}
                data-testid="preview-send-btn"
              >
                <Send className="w-4 h-4 mr-1" />
                Verzenden
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grote viewer voor extra bijlage in voorbeeldweergave */}
      <DocumentLightbox
        open={!!previewLightboxDoc}
        onOpenChange={(o) => { if (!o) setPreviewLightboxDoc(null); }}
        document={previewLightboxDoc}
      />

      {/* Compose New Email Dialog */}
      <Dialog open={composeDialogOpen} onOpenChange={setComposeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Nieuwe Email
            </DialogTitle>
            <DialogDescription>
              Kies een ontvanger en voeg optioneel team of extra adressen toe in CC
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Klant (optioneel)
              </Label>
              <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerSearchOpen}
                    className="w-full justify-between font-normal"
                    data-testid="customer-search"
                  >
                    {composeForm.customer_id
                      ? (() => {
                          const customer = customers.find(c => c.id === composeForm.customer_id);
                          return customer
                            ? `${customer.first_name} ${customer.last_name}`
                            : 'Selecteer klant...';
                        })()
                      : 'Zoek klant op naam...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput 
                      placeholder="Typ klantnaam..." 
                      value={customerSearchQuery}
                      onValueChange={setCustomerSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>Geen klanten gevonden</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="none"
                          onSelect={() => {
                            handleCustomerSelect('none');
                            setCustomerSearchOpen(false);
                            setCustomerSearchQuery('');
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${!composeForm.customer_id ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="text-muted-foreground">-- Geen klant (handmatig) --</span>
                        </CommandItem>
                        {filteredCustomers.slice(0, 20).map((customer) => (
                          <CommandItem
                            key={customer.id}
                            value={customer.id}
                            onSelect={() => {
                              handleCustomerSelect(customer.id);
                              setCustomerSearchOpen(false);
                              setCustomerSearchQuery('');
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${composeForm.customer_id === customer.id ? 'opacity-100' : 'opacity-0'}`} />
                            <div className="flex flex-col">
                              <span className="font-medium">{customer.first_name} {customer.last_name}</span>
                              <span className="text-sm text-muted-foreground">{customer.email}</span>
                            </div>
                          </CommandItem>
                        ))}
                        {filteredCustomers.length > 20 && (
                          <div className="py-2 px-2 text-sm text-muted-foreground text-center">
                            + {filteredCustomers.length - 20} meer resultaten, typ om te filteren...
                          </div>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Manual Email Override - shown when customer selected or for manual entry */}
            {composeForm.customer_id && (
              <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label>Naam</Label>
                  <Input
                    value={composeForm.to_name}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, to_name: e.target.value }))}
                    placeholder="Naam ontvanger"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email adres</Label>
                  <Input
                    type="email"
                    value={composeForm.to_email}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, to_email: e.target.value }))}
                    placeholder="email@voorbeeld.nl"
                  />
                </div>
              </div>
            )}

            {/* Extra e-mailadressen van de klant (koppels / meerdere contactpersonen) */}
            {composeForm.customer_id && (() => {
              const cust = customers.find(c => c.id === composeForm.customer_id);
              const extraEmails = (cust?.additional_emails || []).filter(e => e && e.includes('@'));
              if (extraEmails.length === 0) return null;
              return (
                <div className="pl-4 border-l-2 border-primary/20 space-y-1.5" data-testid="customer-extra-emails">
                  <Label className="text-xs text-muted-foreground">Extra e-mailadressen van deze klant (in CC):</Label>
                  {extraEmails.map((em) => (
                    <label key={em} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ccCustomerExtra.includes(em)}
                        onChange={() => setCcCustomerExtra(prev => 
                          prev.includes(em) ? prev.filter(x => x !== em) : [...prev, em]
                        )}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      {em}
                    </label>
                  ))}
                </div>
              );
            })()}

            {/* Or manual email entry without customer */}
            {!composeForm.customer_id && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Of handmatig email invullen</Label>
                  <Input
                    value={composeForm.to_name}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, to_name: e.target.value }))}
                    placeholder="Naam ontvanger"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email adres</Label>
                  <Input
                    type="email"
                    value={composeForm.to_email}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, to_email: e.target.value }))}
                    placeholder="email@voorbeeld.nl"
                  />
                </div>
              </div>
            )}

            {/* CC Sectie: Team + medewerkers + extra adressen */}
            <div className="space-y-2" data-testid="cc-section">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                CC
              </Label>
              
              <Popover open={ccPopoverOpen} onOpenChange={setCcPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                    data-testid="cc-team-dropdown"
                  >
                    {(ccIncludeTeam || ccTeamEmails.length > 0)
                      ? `${(ccIncludeTeam ? 1 : 0) + ccTeamEmails.length} geselecteerd in CC`
                      : 'Team / medewerkers toevoegen aan CC...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-2" align="start">
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    <label className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer border-b pb-2 mb-1">
                      <input
                        type="checkbox"
                        checked={ccIncludeTeam}
                        onChange={() => setCcIncludeTeam(!ccIncludeTeam)}
                        className="h-4 w-4 rounded border-gray-300"
                        data-testid="cc-team-checkbox"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Team</span>
                        <span className="text-sm text-muted-foreground ml-2">({TEAM_EMAIL})</span>
                      </div>
                    </label>
                    {teamUsers.map((u) => (
                      <label key={u.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer">
                        <input
                          type="checkbox"
                          checked={ccTeamEmails.includes(u.email)}
                          onChange={() => toggleCcTeamEmail(u.email)}
                          className="h-4 w-4 rounded border-gray-300"
                          data-testid={`cc-user-${u.id}`}
                        />
                        <div className="flex-1">
                          <span className="font-medium">{u.first_name} {u.last_name}</span>
                          <span className="text-sm text-muted-foreground ml-2">({u.email})</span>
                        </div>
                        <Badge variant="outline" className="text-xs">{u.role}</Badge>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Vrij extra CC adres toevoegen */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Extra e-mailadres in CC..."
                  value={ccExtraInput}
                  onChange={(e) => setCcExtraInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCcExtraEmail();
                    }
                  }}
                  className="flex-1"
                  data-testid="cc-extra-input"
                />
                <Button type="button" variant="outline" size="icon" onClick={addCcExtraEmail} title="Toevoegen" data-testid="cc-extra-add-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Overzicht geselecteerde CC's */}
              {buildCcList().length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="cc-badges">
                  {buildCcList().map((em) => (
                    <Badge key={em} variant="secondary" className="text-xs gap-1">
                      {em}
                      <button
                        type="button"
                        onClick={() => {
                          if (em === TEAM_EMAIL && ccIncludeTeam) setCcIncludeTeam(false);
                          setCcTeamEmails(prev => prev.filter(x => x !== em));
                          setCcCustomerExtra(prev => prev.filter(x => x !== em));
                          setCcExtraEmails(prev => prev.filter(x => x !== em));
                        }}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label>Onderwerp *</Label>
              <Input
                value={composeForm.subject}
                onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Onderwerp van de email"
                data-testid="compose-subject"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label>Bericht *</Label>
              <HtmlEditor
                value={composeForm.body_html}
                onChange={(html) => setComposeForm(prev => ({ ...prev, body_html: html }))}
                placeholder="Typ hier uw bericht..."
              />
            </div>

            {/* Bijlagen */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Bijlagen
              </Label>
              <Input
                type="file"
                multiple
                onChange={(e) => setComposeFiles(prev => [...prev, ...Array.from(e.target.files || [])])}
                data-testid="compose-attachments-input"
              />
              {composeFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {composeFiles.map((f, i) => (
                    <Badge key={i} variant="secondary" className="text-xs gap-1">
                      <Paperclip className="w-3 h-3" />
                      {f.name} ({(f.size / 1024).toFixed(0)} kB)
                      <button type="button" onClick={() => setComposeFiles(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Bijlagen worden enkel meegestuurd bij "Direct versturen" (max. 15MB totaal).</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeDialogOpen(false)}>
              Annuleren
            </Button>
            <Button variant="outline" onClick={handleQueueComposedEmail} disabled={composing || sendingCompose} data-testid="queue-compose-btn">
              {composing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
              Klaarzetten
            </Button>
            <Button onClick={handleSendComposedEmail} disabled={composing || sendingCompose} data-testid="send-compose-btn">
              {sendingCompose ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Direct versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inbox Email Detail Dialog */}
      <Dialog open={inboxDetailOpen} onOpenChange={setInboxDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedInboxEmail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  {selectedInboxEmail.subject || '(Geen onderwerp)'}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Van:</span>
                    <span>{selectedInboxEmail.from_name || selectedInboxEmail.from_email}</span>
                    <span className="text-muted-foreground">&lt;{selectedInboxEmail.from_email}&gt;</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Datum:</span>
                    <span>{selectedInboxEmail.email_date ? formatDate(selectedInboxEmail.email_date) : '-'}</span>
                  </div>
                  {selectedInboxEmail.customer_name && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Klant:</span>
                      <Badge variant="outline">
                        <User className="w-3 h-3 mr-1" />
                        {selectedInboxEmail.customer_name}
                      </Badge>
                    </div>
                  )}
                  </div>
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 border rounded-lg p-4 bg-white min-h-[200px] overflow-auto max-h-[60vh]">
                {selectedInboxEmail.body_html ? (
                  <div 
                    className="prose prose-sm max-w-none text-black"
                    style={{ color: '#000000' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedInboxEmail.body_html) }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-black">
                    {selectedInboxEmail.body_text || 'Geen inhoud'}
                  </pre>
                )}
              </div>
              {selectedInboxEmail.attachments?.length > 0 && (
                <div className="mt-4">
                  <Label className="flex items-center gap-2 mb-2">
                    <Paperclip className="w-4 h-4" />
                    Bijlagen ({selectedInboxEmail.attachments.length})
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedInboxEmail.attachments.map((att, idx) => (
                      <Badge key={idx} variant="secondary">
                        {att.filename || `Bijlage ${idx + 1}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button
                  onClick={() => {
                    const subj = selectedInboxEmail.subject || '';
                    setReplyForm({ subject: subj.toLowerCase().startsWith('re:') ? subj : `Re: ${subj}`, body: '' });
                    setReplyFiles([]);
                    setReplyCcIncludeTeam(false);
                    setReplyCcTeamEmails([]);
                    setReplyCcExtraEmails([]);
                    setReplyCcExtraInput('');
                    if (teamUsers.length === 0) fetchTeamUsers();
                    setReplyOpen(true);
                  }}
                  data-testid="reply-inbox-btn"
                >
                  <Send className="w-4 h-4 mr-1" />
                  Beantwoorden
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setDeleteInboxId(selectedInboxEmail.id)}
                  data-testid="detail-delete-inbox-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Verwijderen
                </Button>
                <Button variant="outline" onClick={() => setInboxDetailOpen(false)}>Sluiten</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply dialog */}
      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Beantwoorden</DialogTitle>
            <DialogDescription>
              Aan: {selectedInboxEmail?.from_name || selectedInboxEmail?.from_email} &lt;{selectedInboxEmail?.from_email}&gt;
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reply-subject">Onderwerp</Label>
              <Input
                id="reply-subject"
                value={replyForm.subject}
                onChange={(e) => setReplyForm(prev => ({ ...prev, subject: e.target.value }))}
                data-testid="reply-subject-input"
              />
            </div>
            <div>
              <Label>CC (team)</Label>
              <div className="border rounded-md p-2 max-h-36 overflow-y-auto space-y-1 mt-1" data-testid="reply-cc-team-list">
                <label className="flex items-center gap-2 p-1 rounded hover:bg-muted cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={replyCcIncludeTeam}
                    onChange={() => setReplyCcIncludeTeam(!replyCcIncludeTeam)}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="reply-cc-team-checkbox"
                  />
                  <span className="font-medium">Team</span>
                  <span className="text-xs text-muted-foreground">({TEAM_EMAIL})</span>
                </label>
                {teamUsers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 p-1 rounded hover:bg-muted cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={replyCcTeamEmails.includes(u.email)}
                      onChange={() => setReplyCcTeamEmails(prev => prev.includes(u.email) ? prev.filter(e => e !== u.email) : [...prev, u.email])}
                      className="h-4 w-4 rounded border-gray-300"
                      data-testid={`reply-cc-user-${u.id}`}
                    />
                    <span>{u.first_name} {u.last_name}</span>
                    <span className="text-xs text-muted-foreground">({u.email})</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  placeholder="Extra e-mailadres in CC..."
                  value={replyCcExtraInput}
                  onChange={(e) => setReplyCcExtraInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const em = replyCcExtraInput.trim();
                      if (em && em.includes('@')) {
                        setReplyCcExtraEmails(prev => prev.includes(em) ? prev : [...prev, em]);
                        setReplyCcExtraInput('');
                      }
                    }
                  }}
                  className="flex-1"
                  data-testid="reply-cc-extra-input"
                />
                <Button type="button" variant="outline" size="icon" onClick={() => {
                  const em = replyCcExtraInput.trim();
                  if (!em || !em.includes('@')) { toast.error('Voer een geldig e-mailadres in'); return; }
                  setReplyCcExtraEmails(prev => prev.includes(em) ? prev : [...prev, em]);
                  setReplyCcExtraInput('');
                }} title="Toevoegen" data-testid="reply-cc-extra-add-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {buildReplyCcList().length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="reply-cc-badges">
                  {buildReplyCcList().map((em) => (
                    <Badge key={em} variant="secondary" className="text-xs gap-1">
                      {em}
                      <button
                        type="button"
                        onClick={() => {
                          if (em === TEAM_EMAIL) setReplyCcIncludeTeam(false);
                          setReplyCcTeamEmails(prev => prev.filter(x => x !== em));
                          setReplyCcExtraEmails(prev => prev.filter(x => x !== em));
                        }}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="reply-body">Bericht</Label>
              <Textarea
                id="reply-body"
                rows={10}
                placeholder="Typ je antwoord..."
                value={replyForm.body}
                onChange={(e) => setReplyForm(prev => ({ ...prev, body: e.target.value }))}
                data-testid="reply-body-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Het originele bericht wordt automatisch onder je antwoord geciteerd.</p>
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Bijlagen
              </Label>
              <Input
                type="file"
                multiple
                onChange={(e) => setReplyFiles(prev => [...prev, ...Array.from(e.target.files || [])])}
                data-testid="reply-attachments-input"
              />
              {replyFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {replyFiles.map((f, i) => (
                    <Badge key={i} variant="secondary" className="text-xs gap-1">
                      <Paperclip className="w-3 h-3" />
                      {f.name} ({(f.size / 1024).toFixed(0)} kB)
                      <button type="button" onClick={() => setReplyFiles(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyOpen(false)} disabled={sendingReply}>Annuleren</Button>
            <Button
              onClick={async () => {
                if (!replyForm.body.trim()) { toast.error('Typ eerst een bericht'); return; }
                setSendingReply(true);
                try {
                  const bodyHtml = `<div>${replyForm.body.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</div>`;
                  const attachments = await readFilesAsAttachments(replyFiles);
                  await api.post(`/inbox/${selectedInboxEmail.id}/reply`, {
                    subject: replyForm.subject,
                    body_html: bodyHtml,
                    cc_emails: buildReplyCcList(),
                    extra_attachments: attachments
                  });
                  toast.success('Antwoord verzonden');
                  setReplyOpen(false);
                  setInboxDetailOpen(false);
                  fetchInbox();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Kon antwoord niet verzenden');
                } finally {
                  setSendingReply(false);
                }
              }}
              disabled={sendingReply}
              data-testid="reply-send-btn"
            >
              {sendingReply ? 'Verzenden...' : (<><Send className="w-4 h-4 mr-1" />Verstuur antwoord</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grote popup viewer voor bijlage-thumbnails in de edit dialog */}
      <DocumentLightbox
        open={!!editLightboxDoc}
        onOpenChange={(o) => { if (!o) setEditLightboxDoc(null); }}
        document={editLightboxDoc}
      />
    </div>
  );
};

export default Mails;
